import { useCallback, useEffect, useRef, useState } from "react";
import cloneDeep from "lodash.clonedeep";
import { db } from "../db";
import { showConflicts, useReloadItems } from "./draft";
import useLatestRef from "./useLatestRef";
import {
  conflictOf,
  onStoredRow,
  staleOf,
  staleOfConflict,
  versionOf
} from "./baseVersion";

// The version check of a page that writes one stored row at once (see
// baseVersion.js). The page keeps its edits in its own state, as before;
// the hook keeps the BASE, the stored row they were started from, and
// follows MemoryDB:
// - the row changes and the page is clean: onAdopt(row, "follow"), the page
//   shows it (as before);
// - the row moves (a colleague's save or delete) under unsaved edits: the
//   edits stay, `stale` says who and when (ColleagueSaved shows it, with
//   Reload), and a Save is refused by the server;
// - save(remote, payload) sends baseVersion = the version of the base; our
//   row becomes the base (onAdopt(row, "saved")); a 409 opens the conflict
//   dialog ("Discard my changes and reload" = reload()), the edits stay;
// - reload() fetches the stored row and puts it in the page (onAdopt(row,
//   "reload")): the edits are discarded.
//
// accessor, id: the row; live: the row in MemoryDB now (useGetMemItem: a
// new object at every change); isDirty(base): the page has unsaved edits
// (base: the row they were started from), read when the row changes;
// onAdopt(row, reason): put the row (a copy of its own, or undefined when
// not stored) in the page. The remote must be created with
// conflictDialog: true (no error notification for a conflict: the dialog
// says it).
export default function useBaseVersion({
  accessor,
  id,
  live,
  isDirty,
  onAdopt
}) {
  const [base, setBase] = useState(() => cloneDeep(live));
  const baseRef = useRef(base);
  const [stale, setStale] = useState(null);
  const isDirtyRef = useLatestRef(isDirty);
  const onAdoptRef = useLatestRef(onAdopt);
  const reloadItems = useReloadItems();

  const adopt = useCallback(
    (row, reason) => {
      const copy = cloneDeep(row);
      baseRef.current = copy;
      setBase(copy);
      setStale(null);
      // A copy of its own: the page may change it in place
      onAdoptRef.current?.(cloneDeep(row), reason);
    },
    [onAdoptRef]
  );

  useEffect(() => {
    const what = onStoredRow({
      base: baseRef.current,
      live,
      dirty: Boolean(isDirtyRef.current?.(baseRef.current))
    });
    if (what === "adopt") adopt(live, "follow");
    else if (what === "stale") setStale(staleOf(live));
  }, [adopt, isDirtyRef, live]);

  // The stored row, fetched again (a 409 has no data; the socket may have
  // missed the colleague's save), in place of the edits. Rejects when it
  // could not be fetched (the conflict dialog says so and stays open).
  const reload = useCallback(async () => {
    const { failed = [] } = (await reloadItems([{ accessor, id }])) ?? {};
    if (failed.length)
      throw new Error(
        `The stored ${accessor} ${id} could not be loaded: try again.`
      );
    adopt(db.get(accessor, id), "reload");
  }, [accessor, adopt, id, reloadItems]);

  // The version sent with a write of the row (baseVersion)
  const baseVersion = useCallback(() => versionOf(baseRef.current), []);

  // Our write went through: the response is applied to MemoryDB before the
  // callback, so the stored row is ours (with the version the server gave
  // it): the next write is based on it.
  const saved = useCallback(
    () => adopt(db.get(accessor, id), "saved"),
    [accessor, adopt, id]
  );

  // A refused write. A version conflict: the warning stays on the page, the
  // conflict dialog says who and when. Other refusals (main, a row held by
  // a draft, an error) are notified by useDataRemote.
  const refused = useCallback(
    (status, data) => {
      if (data?.type !== "conflict") return;
      setStale(staleOfConflict(conflictOf(data, accessor, id)));
      showConflicts({ conflicts: data.conflicts, onDiscard: reload });
    },
    [accessor, id, reload]
  );

  // remote: a write of useDataSaveItem (conflictDialog: true); payload: the
  // item to save; cb: after our write went through. The baseVersion sent is
  // the base's now, or payload.baseVersion when given: a page that asks a
  // confirm of its own before save() takes the data AND baseVersion() at
  // the click (a colleague's save while the confirm is open moves the base
  // of an unmodified page: the data of the click must not go with their
  // version). A remote with `confirm: true` needs nothing: save() builds the
  // item before the remote asks.
  const save = useCallback(
    (remote, payload, cb) =>
      remote(
        {
          ...payload,
          baseVersion: payload.baseVersion ?? versionOf(baseRef.current)
        },
        (...args) => {
          saved();
          cb && cb(...args);
        },
        refused
      ),
    [refused, saved]
  );

  return { base, stale, baseVersion, save, saved, refused, reload };
}
